import "./Task.scss";
import { useState, useEffect, useRef } from "react";
import { fetcher } from "../../../utils/fetcher";
import Modal from "../../../components/Modal/Modal";
import TaskStatus from "../Modals/TaskStatus/TaskStatus";
import TaskPriority from "../Modals/TaskPriority/TaskPriority";
import Deadline from "../Modals/Deadline/Deadline";
import Responsible from "../Modals/Responsible/Responsible";
import MoveTask from "../Modals/MoveTask/MoveTask";
import RemoveTask from "../Modals/RemoveTask/RemoveTask";
import ConfirmCompleteTask from "../Modals/ConfirmCompleteTask/ConfirmCompleteTask";
import { Link04, Share04 } from "@untitled-ui/icons-react";
import RightSideBarTaskView from "../../../components/RightSideBarContent/RightSideBarTaskView";
import { useSortable } from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getTextPreview } from "../../../utils/textPreview";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";

// Helper function used by both Task and SubtaskRow
const getStatusColor = (status, statusConfig = []) => {
  const statusItem = statusConfig.find((s) => s.label === status);
  return statusItem ? statusItem.color : "transparent";
};

const getPriorityColor = (priority, priorityConfig = []) => {
  const priorityItem = priorityConfig.find((p) => p.order === priority);
  return priorityItem ? priorityItem.color : "transparent";
};

const getPriorityText = (priority, priorityConfig = []) => {
  const priorityItem = priorityConfig.find((p) => p.order === priority);
  return priorityItem ? priorityItem.label : "";
};

const getPriorityTextLegacy = (priority) => {
  switch (priority) {
    case 0:
      return "Urgent";
    case 1:
      return "High";
    case 2:
      return "Medium";
    case 3:
      return "Low";
    case 4:
      return "Trivial";
    default:
      return "";
  }
};

function Task({
  data,
  fetchTasks,
  showRightSideBar,
  hideRightSideBar,
  taskGroups,
  showCompleted,
  subtasks = [],
  selectedTaskId,
  statusConfig = [],
  priorityConfig = [],
}) {
  const [taskData, setTaskData] = useState(data);
  const [editingTitle, setEditingTitle] = useState(data.title);
  const [editingDescription, setEditingDescription] = useState(
    data.description,
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [subtasksExpanded, setSubtasksExpanded] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [localSubtasks, setLocalSubtasks] = useState(subtasks);
  const [pendingToggleIncompleteCount, setPendingToggleIncompleteCount] =
    useState(null);

  // Sync taskData when data prop changes (e.g., after fetchTasks)
  useEffect(() => {
    setTaskData(data);
    setEditingTitle(data.title);
    setEditingDescription(data.description);
  }, [data]);

  // Sync localSubtasks when subtasks prop changes (use JSON.stringify for deep comparison)
  useEffect(() => {
    setLocalSubtasks(subtasks);
  }, [JSON.stringify(subtasks)]);

  // Helper function to determine deadline status
  const getDeadlineStatus = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(0, 0, 0, 0);

    if (deadlineDate < today) return "overdue";
    if (deadlineDate.getTime() === today.getTime()) return "due-today";
    return null;
  };

  const titleInputRef = useRef(null);
  const subtaskInputRef = useRef(null);
  const escapePressedTitle = useRef(false);
  const isAutoExpandedRef = useRef(false);
  const modalRef = useRef();
  const [isTitleOverflowing, setIsTitleOverflowing] = useState(false);

  // Drag and drop
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: data._id,
    disabled: !data.isActive, // Can't drag completed tasks
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Hide original when dragging (DragOverlay shows it)
  };

  // Sensors for subtask drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle subtask reorder with optimistic update
  const handleSubtaskDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const filteredSubtasks = localSubtasks.filter(
      (s) => showCompleted || s.isActive,
    );
    const oldIndex = filteredSubtasks.findIndex((s) => s._id === active.id);
    const newIndex = filteredSubtasks.findIndex((s) => s._id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic update - reorder local state immediately
    const reorderedFiltered = arrayMove(filteredSubtasks, oldIndex, newIndex);

    // Update the full localSubtasks array with new order
    const inactiveSubtasks = localSubtasks.filter(
      (s) => !showCompleted && !s.isActive,
    );
    setLocalSubtasks([...reorderedFiltered, ...inactiveSubtasks]);

    // Update sortOrder for all affected subtasks
    const updates = reorderedFiltered.map((subtask, index) => ({
      _id: subtask._id,
      sortOrder: index,
    }));

    try {
      await fetcher.put("/api/tasks/reorder", updates);
      // Don't call fetchTasks - let the optimistic update stand
      // WebSocket will sync if needed
    } catch (error) {
      console.error("Error reordering subtasks:", error);
      // Revert on error
      setLocalSubtasks(subtasks);
    }
  };

  useEffect(() => {
    setTaskData(data);
    setEditingTitle(data.title);
    setEditingDescription(data.description);

    // Auto-expand if task has subtasks that match search/filter criteria
    if (data.shouldAutoExpand && subtasks.length > 0) {
      console.log(
        "Auto-expanding task:",
        data.title,
        "with",
        subtasks.length,
        "subtasks",
      );
      isAutoExpandedRef.current = true; // Mark as auto-expanded to prevent focus
      setSubtasksExpanded(true);
    }
  }, [
    data._id,
    data.title,
    data.description,
    data.shouldAutoExpand,
    subtasks.length,
  ]);

  // Focus subtask input when expanded (but only if manually expanded, not auto-expanded)
  useEffect(() => {
    if (
      subtasksExpanded &&
      !isAutoExpandedRef.current &&
      subtaskInputRef.current
    ) {
      // Only focus if the user is not currently typing in the search input
      const activeElement = document.activeElement;
      const isSearchInputFocused =
        activeElement?.type === "text" &&
        activeElement?.placeholder?.toLowerCase().includes("search");

      if (!isSearchInputFocused) {
        subtaskInputRef.current.focus();
      }
    }
    // Reset auto-expand flag after handling
    isAutoExpandedRef.current = false;
  }, [subtasksExpanded]);

  // Check if title text is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      if (titleInputRef.current) {
        const isOverflowing =
          titleInputRef.current.scrollWidth > titleInputRef.current.clientWidth;
        setIsTitleOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [editingTitle]);

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;
    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const updateTask = async (taskData) => {
    try {
      // Exclude sortOrder from updates - only the /reorder endpoint should modify sortOrder
      const { sortOrder, ...dataToUpdate } = taskData;
      await fetcher.put(`/api/tasks/${taskData._id}`, dataToUpdate);
      // fetchTasks is called automatically via WebSocket event
    } catch (err) {
      console.error("Error updating task:", err);
      throw err;
    }
  };

  const toggleActive = async (e) => {
    if (isUpdating) return;

    // Check if trying to complete a task that has incomplete subtasks
    if (taskData.isActive && localSubtasks.length > 0) {
      const incompleteSubtasks = localSubtasks.filter(
        (st) => st.isActive && st.status !== "Completed",
      );
      if (incompleteSubtasks.length > 0) {
        // Show modal confirmation instead of alert
        setPendingToggleIncompleteCount(incompleteSubtasks.length);
        if (e) {
          openModal(
            e,
            "center-center",
            <ConfirmCompleteTask
              incompleteCount={incompleteSubtasks.length}
              onConfirm={() => {
                modalRef.current?.close();
                setPendingToggleIncompleteCount(null);
                performToggleActive();
              }}
              onCancel={() => {
                modalRef.current?.close();
                setPendingToggleIncompleteCount(null);
              }}
            />,
          );
        }
        return;
      }
    }

    // No incomplete subtasks, proceed directly
    await performToggleActive();
  };

  const performToggleActive = async () => {
    setIsUpdating(true);
    const updated = { ...taskData, isActive: !taskData.isActive };
    setTaskData(updated);
    try {
      await updateTask(updated);
      // Refresh tasks to update the filtered view (show/hide completed)
      if (fetchTasks) {
        fetchTasks();
      }
    } catch (err) {
      console.error("Failed to update task", err);
      // Revert local state on error
      setTaskData(taskData);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (isUpdating) return;
    setIsUpdating(true);
    const updated = { ...taskData, status: newStatus };
    setTaskData(updated);
    try {
      await updateTask(updated);
    } catch (err) {
      console.error("Failed to update task status", err);
    } finally {
      setIsUpdating(false);
      modalRef.current?.close();
    }
  };

  const updatePriority = async (newPriority) => {
    if (isUpdating) return;
    setIsUpdating(true);
    const updated = { ...taskData, priority: newPriority };
    setTaskData(updated);
    try {
      await updateTask(updated);
    } catch (err) {
      console.error("Failed to update task priority", err);
    } finally {
      setIsUpdating(false);
      modalRef.current?.close();
    }
  };

  const updateDeadline = async (newDeadline) => {
    if (isUpdating) return;
    setIsUpdating(true);
    const updated = { ...taskData, deadline: newDeadline };
    setTaskData(updated);
    try {
      await updateTask(updated);
    } catch (err) {
      console.error("Failed to update task deadline", err);
    } finally {
      setIsUpdating(false);
      modalRef.current?.close();
    }
  };

  const updateResponsible = async (newResponsible) => {
    if (isUpdating) return;
    setIsUpdating(true);

    const updated = {
      ...taskData,
      responsible: newResponsible
        ? {
            _id: newResponsible.userId,
            name: `${newResponsible.firstName} ${newResponsible.lastName}`,
            profile: newResponsible.profile,
          }
        : null,
    };

    setTaskData(updated);
    try {
      await updateTask(updated);
    } catch (err) {
      console.error("Failed to update responsible", err);
    } finally {
      setIsUpdating(false);
      modalRef.current?.close();
    }
  };

  const removeTask = async () => {
    try {
      await fetcher.put(`/api/tasks/${taskData._id}`, {
        isActive: false,
      });
      if (fetchTasks) fetchTasks();
      if (modalRef.current) modalRef.current.close();
    } catch (err) {
      console.error("Failed to remove task", err);
    }
  };

  const cancelRemoveTask = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const createSubtask = async (subtaskData) => {
    try {
      await fetcher.post("/api/tasks", subtaskData);
      // fetchTasks is called automatically via WebSocket event
    } catch (err) {
      console.error("Error creating subtask:", err);
      throw err;
    }
  };

  const handleAddSubtask = async (e) => {
    if (e.key === "Enter" && newSubtaskTitle.trim() !== "") {
      console.log("Creating subtask for task:", taskData);
      const subtaskData = {
        groupId: taskData.sectionId || taskData.groupId,
        title: newSubtaskTitle.trim(),
        responsible: "",
        status: "Not Started",
        description: "",
        isActive: true,
        parentTaskId: taskData._id || taskData.id,
        isSubtask: true,
        priority: taskData.priority || 4,
      };

      console.log("Subtask data:", subtaskData);

      try {
        await createSubtask(subtaskData);
        setNewSubtaskTitle("");
        // Don't call fetchTasks - WebSocket will handle the update
      } catch (err) {
        console.error("Error creating subtask:", err);
        console.error("Full error details:", err.response || err);
      }
    } else if (e.key === "Escape") {
      setNewSubtaskTitle("");
      setSubtasksExpanded(false);
    }
  };

  const handleTextField = ({
    field,
    value,
    setValue,
    originalValue,
    inputRef,
    escapeRef,
  }) => ({
    onChange: (e) => setValue(e.target.value),
    onKeyDown: (e) => {
      if (e.key === "Escape") {
        escapeRef.current = true;
        setValue(originalValue);
        inputRef.current.blur();
      } else if (e.key === "Enter") {
        inputRef.current.blur();
      }
    },
    onBlur: async () => {
      if (escapeRef.current) {
        escapeRef.current = false;
        return;
      }
      if (value.trim() !== "" && value !== originalValue) {
        const updated = { ...taskData, [field]: value };
        setTaskData(updated);
        try {
          await updateTask(updated);
        } catch (err) {
          console.error(`Failed to update ${field}`, err);
        }
      }
    },
  });

  const titleProps = handleTextField({
    field: "title",
    value: editingTitle,
    setValue: setEditingTitle,
    originalValue: taskData.title,
    inputRef: titleInputRef,
    escapeRef: escapePressedTitle,
  });

  // const descProps = handleTextField({
  //   field: "description",
  //   value: editingDescription,
  //   setValue: setEditingDescription,
  //   originalValue: taskData.description,
  //   inputRef: descInputRef,
  //   escapeRef: escapePressedDesc,
  // });

  // Handler for updating description from TipTap editor (RightSideBarTaskView)
  // Note: This only updates local state - the sidebar handles the actual save
  const handleDescriptionUpdate = (newDescription) => {
    setEditingDescription(newDescription);
    const updated = { ...taskData, description: newDescription };
    setTaskData(updated);
  };

  return (
    <>
      <div
        className={`task ${taskData.isActive ? getDeadlineStatus(taskData.deadline) || "" : ""} ${selectedTaskId === taskData._id ? "selected" : ""}`}
        ref={setNodeRef}
        style={style}
      >
        <Modal ref={modalRef} />

        <div className="checked">
          <label className="custom-checkbox">
            <input
              type="checkbox"
              checked={!taskData.isActive}
              onChange={toggleActive}
            />
            <span className="checkmark"></span>
          </label>
        </div>

        <div
          className={`subtasks-toggle ${!taskData.isActive ? "inactive" : ""}`}
          onClick={() => setSubtasksExpanded(!subtasksExpanded)}
          style={{ cursor: "pointer" }}
        >
          <span
            className={`material-symbols-outlined ${subtasks.length > 0 ? "has-content" : ""}`}
            style={{
              fontSize: "20px",
              color: subtasks.length > 0 ? "#2196f3" : data.color,
            }}
          >
            {subtasksExpanded ? "keyboard_arrow_down" : "chevron_right"}
          </span>
        </div>

        <div className={`title ${!taskData.isActive ? "inactive" : ""}`}>
          <input
            type="text"
            className={`title ${!taskData.isActive ? "inactive" : ""}`}
            ref={titleInputRef}
            value={editingTitle}
            readOnly={!taskData.isActive}
            title={isTitleOverflowing ? editingTitle : ""}
            {...titleProps}
          />
        </div>

        <div
          className={`status ${!taskData.isActive ? "inactive" : ""}`}
          style={{
            backgroundColor: getStatusColor(
              taskData?.status ?? "",
              statusConfig,
            ),
          }}
          onClick={(e) => {
            if (taskData.isActive) {
              openModal(
                e,
                "auto-top-center",
                <TaskStatus
                  updateStatus={updateStatus}
                  statusConfig={statusConfig}
                />,
              );
            }
          }}
        >
          {taskData?.status ?? ""}
        </div>

        <div
          className={`responsible ${!taskData.isActive ? "inactive" : ""}`}
          onClick={(e) => {
            if (taskData.isActive) {
              openModal(
                e,
                "auto-top-center",
                <Responsible
                  updateResponsible={updateResponsible}
                  selectedResponsible={
                    taskData.responsible && taskData.responsible.name
                      ? {
                          userId: taskData.responsible._id,
                          firstName:
                            taskData.responsible.name.split(" ")[0] || "",
                          lastName:
                            taskData.responsible.name
                              .split(" ")
                              .slice(1)
                              .join(" ") || "",
                        }
                      : null
                  }
                />,
              );
            }
          }}
        >
          {taskData?.responsible?.name ? (
            <UserAvatar
              user={{
                email: taskData.responsible.email,
                firstName: taskData.responsible.name.split(" ")[0],
                lastName: taskData.responsible.name
                  .split(" ")
                  .slice(1)
                  .join(" "),
                profile: taskData.responsible.profile,
              }}
              size="medium"
              className="responsible-avatar"
            />
          ) : (
            ""
          )}
        </div>

        <div
          className={`links ${!taskData.isActive ? "inactive" : ""}`}
          onClick={() => {
            if (taskData.isActive) {
              showRightSideBar(
                <RightSideBarTaskView
                  taskData={taskData}
                  hideRightSideBar={hideRightSideBar}
                  setEditingDescription={handleDescriptionUpdate}
                  activeTab={1}
                />,
                taskData,
                1, // Tab index for links
              );
            }
          }}
        >
          <span
            className={`material-symbols-outlined ${taskData.links && taskData.links.length > 0 ? "has-content" : ""}`}
            style={{
              fontSize: "20px",
              color:
                taskData.links && taskData.links.length > 0
                  ? "#2196f3"
                  : data.color,
            }}
          >
            link
          </span>
        </div>

        <div
          className={`images ${!taskData.isActive ? "inactive" : ""}`}
          onClick={() => {
            if (taskData.isActive) {
              showRightSideBar(
                <RightSideBarTaskView
                  taskData={taskData}
                  hideRightSideBar={hideRightSideBar}
                  setEditingDescription={handleDescriptionUpdate}
                  activeTab={2}
                />,
                taskData,
                2, // Tab index for images
              );
            }
          }}
        >
          <span
            className={`material-symbols-outlined ${taskData.images && taskData.images.length > 0 ? "has-content" : ""}`}
            style={{
              fontSize: "20px",
              color:
                taskData.images && taskData.images.length > 0
                  ? "#2196f3"
                  : data.color,
            }}
          >
            image
          </span>
        </div>

        <div
          className={`deadline ${!taskData.isActive ? "inactive" : ""}`}
          onClick={(e) => {
            if (taskData.isActive) {
              openModal(
                e,
                "auto-top-center",
                <Deadline
                  updateDeadline={updateDeadline}
                  selectedDeadline={taskData.deadline}
                />,
              );
            }
          }}
        >
          {taskData.deadline
            ? new Date(taskData.deadline).toLocaleDateString("da-DK", {
                day: "numeric",
                month: "short",
              })
            : ""}
        </div>

        <div
          className={`priority ${!taskData.isActive ? "inactive" : ""}`}
          style={{
            backgroundColor: getPriorityColor(
              taskData?.priority,
              priorityConfig,
            ),
          }}
          onClick={(e) => {
            if (taskData.isActive) {
              openModal(
                e,
                "auto-top-center",
                <TaskPriority
                  updatePriority={updatePriority}
                  priorityConfig={priorityConfig}
                />,
              );
            }
          }}
        >
          {getPriorityText(taskData?.priority, priorityConfig)}
        </div>

        <div
          className={`description ${!taskData.isActive ? "inactive" : ""}`}
          onClick={() =>
            showRightSideBar(
              <RightSideBarTaskView
                taskData={taskData}
                hideRightSideBar={hideRightSideBar}
                setEditingDescription={handleDescriptionUpdate}
                activeTab={0}
                focusAtEnd={true}
              />,
              taskData,
              0, // Tab index for description
            )
          }
        >
          <span className="description-text">
            {getTextPreview(editingDescription)}
          </span>
        </div>
        <div
          className={`move ${!taskData.isActive ? "inactive" : ""}`}
          style={{
            cursor: taskData.isActive ? "pointer" : "default",
          }}
          onClick={(e) => {
            if (taskData.isActive && taskGroups && taskGroups.length > 0) {
              openModal(
                e,
                "right-center",
                <MoveTask
                  task={taskData}
                  taskGroups={taskGroups}
                  closeMoveTask={() => {
                    if (modalRef.current) modalRef.current.close();
                  }}
                  onSave={fetchTasks}
                />,
              );
            }
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "20px", color: data.color }}
          >
            move_item
          </span>
        </div>
        <div
          className={`remove ${!taskData.isActive ? "inactive" : ""}`}
          style={{
            cursor: taskData.isActive ? "pointer" : "default",
          }}
          onClick={(e) => {
            if (taskData.isActive) {
              openModal(
                e,
                "center-center",
                <RemoveTask
                  removeTask={removeTask}
                  cancelRemoveTask={cancelRemoveTask}
                />,
              );
            }
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "20px", color: data.color }}
          >
            delete
          </span>
        </div>
        <div
          className={`drag ${!taskData.isActive ? "inactive" : ""}`}
          {...attributes}
          {...listeners}
          style={{
            cursor: taskData.isActive ? "grab" : "default",
            touchAction: "none",
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "20px", color: data.color }}
          >
            drag_indicator
          </span>
        </div>
      </div>

      {/* Render subtasks with drag and drop */}
      {subtasksExpanded && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSubtaskDragEnd}
        >
          <SortableContext
            items={localSubtasks
              .filter((subtask) => showCompleted || subtask.isActive)
              .map((s) => s._id)}
            strategy={verticalListSortingStrategy}
          >
            {localSubtasks
              .filter((subtask) => showCompleted || subtask.isActive)
              .map((subtask) => (
                <SubtaskRow
                  key={subtask._id}
                  subtask={subtask}
                  data={data}
                  fetchTasks={fetchTasks}
                  showRightSideBar={showRightSideBar}
                  selectedTaskId={selectedTaskId}
                  statusConfig={statusConfig}
                  priorityConfig={priorityConfig}
                />
              ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Subtask input row - shown after all subtasks */}
      {subtasksExpanded && (
        <div
          className="subtask-row"
          style={{
            display: "grid",
            gridColumn: "1 / -1",
            gridTemplateColumns: "subgrid",
            alignItems: "center",
          }}
        >
          <div></div>
          <div></div>
          <div className="new-subtask">
            <input
              type="text"
              className="new-subtask"
              ref={subtaskInputRef}
              placeholder="+ Add Subtask"
              value={newSubtaskTitle}
              onChange={(e) => setNewSubtaskTitle(e.target.value)}
              onKeyDown={handleAddSubtask}
            />
          </div>
        </div>
      )}
    </>
  );
}

// Separate component for subtask rows to have individual modal refs
function SubtaskRow({
  subtask,
  data,
  fetchTasks,
  showRightSideBar,
  selectedTaskId,
  statusConfig = [],
  priorityConfig = [],
}) {
  const subtaskModalRef = useRef();
  const subtaskTitleInputRef = useRef();
  const subtaskEscapePressedRef = useRef(false);
  const [editingSubtaskTitle, setEditingSubtaskTitle] = useState(subtask.title);
  const [isSubtaskTitleOverflowing, setIsSubtaskTitleOverflowing] =
    useState(false);

  // Drag and drop for subtask
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: subtask._id,
    disabled: !subtask.isActive,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Helper function to determine deadline status
  const getDeadlineStatus = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(0, 0, 0, 0);

    if (deadlineDate < today) return "overdue";
    if (deadlineDate.getTime() === today.getTime()) return "due-today";
    return null;
  };

  // Update local state when subtask prop changes
  useEffect(() => {
    setEditingSubtaskTitle(subtask.title);
  }, [subtask.title]);

  // Check if subtask title text is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      if (subtaskTitleInputRef.current) {
        const isOverflowing =
          subtaskTitleInputRef.current.scrollWidth >
          subtaskTitleInputRef.current.clientWidth;
        setIsSubtaskTitleOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [editingSubtaskTitle]);

  const openSubtaskModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;
    if (subtaskModalRef.current) {
      subtaskModalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const updateSubtaskStatus = async (newStatus) => {
    const { sortOrder, ...subtaskWithoutSort } = subtask;
    const updated = { ...subtaskWithoutSort, status: newStatus };
    try {
      await fetcher.put(`/api/tasks/${subtask._id}`, updated);
      if (fetchTasks) fetchTasks();
    } catch (err) {
      console.error("Failed to update subtask status", err);
    } finally {
      subtaskModalRef.current?.close();
    }
  };

  const updateSubtaskPriority = async (newPriority) => {
    const { sortOrder, ...subtaskWithoutSort } = subtask;
    const updated = { ...subtaskWithoutSort, priority: newPriority };
    try {
      await fetcher.put(`/api/tasks/${subtask._id}`, updated);
      if (fetchTasks) fetchTasks();
    } catch (err) {
      console.error("Failed to update subtask priority", err);
    } finally {
      subtaskModalRef.current?.close();
    }
  };

  const updateSubtaskDeadline = async (newDeadline) => {
    const { sortOrder, ...subtaskWithoutSort } = subtask;
    const updated = { ...subtaskWithoutSort, deadline: newDeadline };
    try {
      await fetcher.put(`/api/tasks/${subtask._id}`, updated);
      if (fetchTasks) fetchTasks();
    } catch (err) {
      console.error("Failed to update subtask deadline", err);
    } finally {
      subtaskModalRef.current?.close();
    }
  };

  const updateSubtaskResponsible = async (newResponsible) => {
    const { sortOrder, ...subtaskWithoutSort } = subtask;
    const updated = {
      ...subtaskWithoutSort,
      responsible: newResponsible
        ? {
            _id: newResponsible.userId,
            name: `${newResponsible.firstName} ${newResponsible.lastName}`,
            profile: newResponsible.profile,
          }
        : null,
    };
    try {
      await fetcher.put(`/api/tasks/${subtask._id}`, updated);
      if (fetchTasks) fetchTasks();
    } catch (err) {
      console.error("Failed to update subtask responsible", err);
    } finally {
      subtaskModalRef.current?.close();
    }
  };

  const removeSubtask = async () => {
    try {
      await fetcher.put(`/api/tasks/${subtask._id}`, {
        isActive: false,
      });
      if (fetchTasks) fetchTasks();
      if (subtaskModalRef.current) subtaskModalRef.current.close();
    } catch (err) {
      console.error("Failed to remove subtask", err);
    }
  };

  const cancelRemoveSubtask = () => {
    if (subtaskModalRef.current) subtaskModalRef.current.close();
  };

  return (
    <div
      ref={setNodeRef}
      className={`task subtask ${!subtask.isActive ? "inactive" : ""} ${subtask.isActive ? getDeadlineStatus(subtask.deadline) || "" : ""} ${selectedTaskId === subtask._id ? "selected" : ""}`}
      style={{
        display: "grid",
        gridColumn: "1 / -1",
        gridTemplateColumns: "subgrid",
        alignItems: "center",
        ...style,
      }}
    >
      <Modal ref={subtaskModalRef} />

      {/* Empty cell for subtask (chevron column position) */}
      <div className="subtask-spacer">&nbsp;</div>

      {/* Checkbox */}
      <div className="checked">
        <label className="custom-checkbox">
          <input
            type="checkbox"
            checked={!subtask.isActive}
            onChange={async () => {
              const { sortOrder, ...subtaskWithoutSort } = subtask;
              const updated = {
                ...subtaskWithoutSort,
                isActive: !subtask.isActive,
              };
              try {
                await fetcher.put(`/api/tasks/${subtask._id}`, updated);
                if (fetchTasks) fetchTasks();
              } catch (err) {
                console.error("Failed to update subtask", err);
              }
            }}
          />
          <span className="checkmark"></span>
        </label>
      </div>

      <div className={`title ${!subtask.isActive ? "inactive" : ""}`}>
        <input
          type="text"
          className={`title ${!subtask.isActive ? "inactive" : ""}`}
          ref={subtaskTitleInputRef}
          value={editingSubtaskTitle}
          readOnly={!subtask.isActive}
          title={isSubtaskTitleOverflowing ? editingSubtaskTitle : ""}
          onChange={(e) => setEditingSubtaskTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              subtaskEscapePressedRef.current = true;
              setEditingSubtaskTitle(subtask.title);
              subtaskTitleInputRef.current.blur();
            } else if (e.key === "Enter") {
              subtaskTitleInputRef.current.blur();
            }
          }}
          onBlur={async () => {
            if (subtaskEscapePressedRef.current) {
              subtaskEscapePressedRef.current = false;
              return;
            }
            if (
              editingSubtaskTitle.trim() !== "" &&
              editingSubtaskTitle !== subtask.title
            ) {
              const { sortOrder, ...subtaskWithoutSort } = subtask;
              const updated = {
                ...subtaskWithoutSort,
                title: editingSubtaskTitle,
              };
              try {
                await fetcher.put(`/api/tasks/${subtask._id}`, updated);
                // Don't call fetchTasks here - let WebSocket handle it
              } catch (err) {
                console.error("Failed to update subtask title", err);
                // Revert on error
                setEditingSubtaskTitle(subtask.title);
              }
            }
          }}
        />
      </div>

      <div
        className={`status ${!subtask.isActive ? "inactive" : ""}`}
        style={{
          backgroundColor: getStatusColor(subtask?.status ?? "", statusConfig),
        }}
        onClick={(e) => {
          if (subtask.isActive) {
            openSubtaskModal(
              e,
              "auto-top-center",
              <TaskStatus
                updateStatus={updateSubtaskStatus}
                statusConfig={statusConfig}
              />,
            );
          }
        }}
      >
        {subtask?.status ?? ""}
      </div>

      <div
        className={`responsible ${!subtask.isActive ? "inactive" : ""}`}
        onClick={(e) => {
          if (subtask.isActive) {
            openSubtaskModal(
              e,
              "auto-top-center",
              <Responsible
                updateResponsible={updateSubtaskResponsible}
                selectedResponsible={
                  subtask.responsible && subtask.responsible.name
                    ? {
                        userId: subtask.responsible._id,
                        firstName: subtask.responsible.name.split(" ")[0] || "",
                        lastName:
                          subtask.responsible.name
                            .split(" ")
                            .slice(1)
                            .join(" ") || "",
                      }
                    : null
                }
              />,
            );
          }
        }}
      >
        {subtask?.responsible?.name ? (
          <UserAvatar
            user={{
              email: subtask.responsible.email,
              firstName: subtask.responsible.name.split(" ")[0],
              lastName: subtask.responsible.name.split(" ").slice(1).join(" "),
              profile: subtask.responsible.profile,
            }}
            size="medium"
            className="responsible-avatar"
          />
        ) : (
          ""
        )}
      </div>

      <div
        className={`links ${!subtask.isActive ? "inactive" : ""}`}
        onClick={() => {
          if (subtask.isActive) {
            showRightSideBar(
              <RightSideBarTaskView
                taskData={subtask}
                hideRightSideBar={() => {}}
                setEditingDescription={async (newDescription) => {
                  const { sortOrder, ...subtaskWithoutSort } = subtask;
                  const updated = {
                    ...subtaskWithoutSort,
                    description: newDescription,
                  };
                  try {
                    await fetcher.put(`/api/tasks/${subtask._id}`, updated);
                    if (fetchTasks) fetchTasks();
                  } catch (err) {
                    console.error("Failed to update subtask", err);
                  }
                }}
                activeTab={1}
                focusAtEnd={false}
              />,
              subtask,
              1, // Tab index for links
            );
          }
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: "20px",
            color:
              subtask.links && subtask.links.length > 0
                ? "#2196f3"
                : data.color,
          }}
        >
          link
        </span>
      </div>

      <div
        className={`images ${!subtask.isActive ? "inactive" : ""}`}
        onClick={() => {
          if (subtask.isActive) {
            showRightSideBar(
              <RightSideBarTaskView
                taskData={subtask}
                hideRightSideBar={() => {}}
                setEditingDescription={async (newDescription) => {
                  const { sortOrder, ...subtaskWithoutSort } = subtask;
                  const updated = {
                    ...subtaskWithoutSort,
                    description: newDescription,
                  };
                  try {
                    await fetcher.put(`/api/tasks/${subtask._id}`, updated);
                    if (fetchTasks) fetchTasks();
                  } catch (err) {
                    console.error("Failed to update subtask", err);
                  }
                }}
                activeTab={2}
                focusAtEnd={false}
              />,
              subtask,
              2, // Tab index for images
            );
          }
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: "20px",
            color:
              subtask.images && subtask.images.length > 0
                ? "#2196f3"
                : data.color,
          }}
        >
          image
        </span>
      </div>

      <div
        className={`deadline ${!subtask.isActive ? "inactive" : ""}`}
        onClick={(e) => {
          if (subtask.isActive) {
            openSubtaskModal(
              e,
              "auto-top-center",
              <Deadline
                updateDeadline={updateSubtaskDeadline}
                selectedDeadline={subtask.deadline}
              />,
            );
          }
        }}
      >
        {subtask.deadline
          ? new Date(subtask.deadline).toLocaleDateString("da-DK", {
              day: "numeric",
              month: "short",
            })
          : ""}
      </div>

      <div
        className={`priority ${!subtask.isActive ? "inactive" : ""}`}
        style={{
          backgroundColor: getPriorityColor(subtask?.priority, priorityConfig),
        }}
        onClick={(e) => {
          if (subtask.isActive) {
            openSubtaskModal(
              e,
              "auto-top-center",
              <TaskPriority
                updatePriority={updateSubtaskPriority}
                priorityConfig={priorityConfig}
              />,
            );
          }
        }}
      >
        {getPriorityText(subtask?.priority, priorityConfig)}
      </div>

      <div
        className={`description ${!subtask.isActive ? "inactive" : ""}`}
        onClick={() =>
          showRightSideBar(
            <RightSideBarTaskView
              taskData={subtask}
              hideRightSideBar={() => {}}
              setEditingDescription={async (newDescription) => {
                const { sortOrder, ...subtaskWithoutSort } = subtask;
                const updated = {
                  ...subtaskWithoutSort,
                  description: newDescription,
                };
                try {
                  await fetcher.put(`/api/tasks/${subtask._id}`, updated);
                  if (fetchTasks) fetchTasks();
                } catch (err) {
                  console.error("Failed to update subtask description", err);
                }
              }}
              activeTab={0}
              focusAtEnd={true}
            />,
            subtask,
            0, // Tab index for description
          )
        }
      >
        <span className="description-text">
          {getTextPreview(subtask.description)}
        </span>
      </div>

      <div className={`move ${!subtask.isActive ? "inactive" : ""}`}></div>
      <div
        className={`remove ${!subtask.isActive ? "inactive" : ""}`}
        style={{
          cursor: subtask.isActive ? "pointer" : "default",
        }}
        onClick={(e) => {
          if (subtask.isActive) {
            openSubtaskModal(
              e,
              "center-center",
              <RemoveTask
                removeTask={removeSubtask}
                cancelRemoveTask={cancelRemoveSubtask}
              />,
            );
          }
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: "20px", color: data.color }}
        >
          delete
        </span>
      </div>
      {/* Drag handle for subtask */}
      <div
        className={`drag ${!subtask.isActive ? "inactive" : ""}`}
        {...attributes}
        {...listeners}
        style={{
          cursor: subtask.isActive ? "grab" : "default",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: "20px", color: data.color }}
        >
          drag_indicator
        </span>
      </div>
    </div>
  );
}

export default Task;
